/* eslint-disable no-underscore-dangle */
import { getOrderLineKey } from './orderLineKey';

// Order-line kinds that represent OPERATOR-side movement (we → operator)
// rather than customer-side debt (customer → us). The two sets are
// complementary — a payment from a customer can only hit customer-side
// lines (so operator-kind ones get filtered out); an outbound operator
// payout can only hit operator-side lines (so the reverse filter
// applies). One source of truth for both.
const OPERATOR_SIDE_KINDS = new Set([
  'operator', 'operator_cancellation', 'cash_from_client',
]);
const CUSTOMER_SIDE_SKIP_KINDS = OPERATOR_SIDE_KINDS;

const LINE_KIND_META = {
  base: { label: 'Base price', variant: 'primary' },
  service: { label: 'Service', variant: 'success' },
  partner_cancellation: { label: 'Cancellation (partner)', variant: 'secondary' },
  adjustment: { label: 'Adjustment', variant: 'dark' },
  // operator / operator_cancellation / cash_from_client are filtered out
  // before display, but keep entries so callers can describe them if
  // they ever surface (e.g. an audit view).
  operator: { label: 'Operator', variant: 'info' },
  operator_cancellation: { label: 'Cancellation (operator)', variant: 'secondary' },
  cash_from_client: { label: 'Cash collected', variant: 'warning' },
};

// Short human-readable label for an order line — mirrors the inline
// describeLine helper in RecordPaymentModal so the picker reads the
// same across both modals.
export function describeLine(line) {
  if (line.kind === 'service') {
    return line.metadata?.service_name || line.description || 'Service';
  }
  if (line.kind === 'operator') {
    const role = line.metadata?.role;
    if (role) return `Operator (${role})`;
    return line.description || 'Operator';
  }
  if (line.kind === 'cash_from_client') {
    return line.description || 'Cash from client';
  }
  if (line.kind === 'base') return line.description || 'Base price';
  if (line.kind === 'adjustment') return line.description || 'Adjustment';
  return line.description || (line.kind || 'Line');
}

export function lineKindLabel(line) {
  return (LINE_KIND_META[line.kind] || LINE_KIND_META.adjustment).label;
}

/**
 * Derive the set of order lines a CUSTOMER payment can be allocated
 * against, given a booking's `order_lines[]` array and the booking
 * itself (for the legacy fallback).
 *
 * Returns lines tagged with two computed fields:
 *   `_key`         — stable identifier (mirrors backend OrderLineKey helper)
 *   `_balanceNum`  — customer-side owed amount (debit - credit, 0 for
 *                    operator-side kinds so they get filtered out)
 *
 * If the booking has NO ledger-side `order_lines[]` (pre-OrderLinesBuilder
 * legacy data), a single synthetic `base` line is materialised from
 * `booking.total_price` (or `partner_cancellation_price` when the
 * booking is cancelled). The `_key='base'` matches the server-side
 * fallback so future re-saves stay attribution-stable.
 *
 * Pass `{ includeZeroBalance: true }` when you want the picker to also
 * include already-paid lines (so the operator can SEE all lines, even
 * if there's nothing left owed — useful for forensic re-allocation).
 * Default false matches the RecordPaymentModal view ("show only what
 * still has a balance").
 */
export function computeAllocatableLines(booking, options = {}) {
  // `paidByLineKey`: optional map of `order_line_key → already-paid
  // amount in the line's currency`. Decorates each line's
  // `_balanceNum` with "remaining owed after prior payments" so the
  // picker label honestly reads "owes €15" on a line that's already
  // received €5 of its €20 debit, rather than the misleading "owes
  // €20" that ignores existing allocations.
  const { includeZeroBalance = false, paidByLineKey = null } = options;
  const orderLines = Array.isArray(booking?.order_lines) ? booking.order_lines : [];

  const fromLedger = orderLines
    .map((line, idx) => {
      const kind = line.kind || 'adjustment';
      const key = getOrderLineKey(line, idx);
      const debit = Number(line.debit) || 0;
      const credit = Number(line.credit) || 0;
      const owed = debit - credit;
      const alreadyPaid = paidByLineKey && Number.isFinite(paidByLineKey[key])
        ? paidByLineKey[key]
        : 0;
      const remaining = owed - alreadyPaid;
      return {
        ...line,
        _key: key,
        _balanceNum: CUSTOMER_SIDE_SKIP_KINDS.has(kind) ? 0 : remaining,
      };
    })
    .filter((line) => (includeZeroBalance
      ? !CUSTOMER_SIDE_SKIP_KINDS.has(line.kind || 'adjustment')
      : line._balanceNum > 0.005));

  if (fromLedger.length > 0) return fromLedger;

  // Synthetic fallback for legacy bookings without order_lines[].
  if (!booking) return fromLedger;
  const isCancelled = booking.status === 'Cancelled';
  const total = isCancelled
    ? Number(booking.partner_cancellation_price ?? 0)
    : Number(booking.total_price ?? booking.price ?? 0);
  if (!(total > 0)) return fromLedger;
  return [{
    kind: 'base',
    debit: total,
    credit: 0,
    credit_operator: 0,
    currency: booking.currency || 'EUR',
    description: isCancelled ? 'Partner cancellation' : 'Base price',
    balance: total,
    _key: 'base',
    _balanceNum: total,
    _synthetic: true,
  }];
}

/**
 * Operator-side mirror of `computeAllocatableLines`. Returns the lines
 * an outbound operator payout can be attributed to: kind ∈ {operator,
 * operator_cancellation, cash_from_client} with non-zero credit_operator.
 *
 * `_key` is set, `_balanceNum` carries the credit_operator amount in
 * the line's own currency (HUF for the typical settlement-currency
 * operator). FX conversion lives client-side via
 * `credit_operator_in_booking_currency` when callers need a booking-
 * currency view.
 */
export function computeAllocatableOperatorLines(booking, options = {}) {
  const { includeZeroBalance = false } = options;
  const orderLines = Array.isArray(booking?.order_lines) ? booking.order_lines : [];

  return orderLines
    .map((line, idx) => {
      const owed = Number(line.credit_operator) || 0;
      return {
        ...line,
        _key: getOrderLineKey(line, idx),
        _balanceNum: OPERATOR_SIDE_KINDS.has(line.kind || '') ? owed : 0,
      };
    })
    .filter((line) => (includeZeroBalance
      ? OPERATOR_SIDE_KINDS.has(line.kind || '')
      : line._balanceNum > 0.005));
}

export { CUSTOMER_SIDE_SKIP_KINDS, OPERATOR_SIDE_KINDS, LINE_KIND_META };
